import React, { useEffect } from 'react';
import { KEYS } from 'platejs';
import { usePlateEditor, Plate, ParagraphPlugin, PlateLeaf } from 'platejs/react';
import { BoldPlugin, ItalicPlugin, StrikethroughPlugin, CodePlugin, HeadingPlugin } from '@platejs/basic-nodes/react';
import { ListPlugin } from '@platejs/list-classic/react';
import { LinkPlugin } from '@platejs/link/react';
import { ClassNames, css } from '@emotion/react';
import { fonts, lengths, zIndex } from 'decap-cms-ui-default';
import { fromJS } from 'immutable';
import { editorContainerStyles, EditorControlBar, editorStyleVars } from '../styles';
import { markdownToSlate, slateToMarkdown } from '../serializers';
import Editor from './components/Editor';
import Toolbar from './components/Toolbar';
import ParagraphElement from './components/Element/ParagraphElement';
import withProps from './withProps';
import CodeLeaf from './components/Leaf/CodeLeaf';
import HeadingElement from './components/Element/HeadingElement';
import ListElement from './components/Element/ListElement';
import BlockquoteElement from './components/Element/BlockquoteElement';
import LinkElement from './components/Element/LinkElement';
import ImageElement from './components/Element/ImageElement';
import ExtendedBlockquotePlugin from './plugins/ExtendedBlockquotePlugin';
import ImagePlugin from './plugins/ImagePlugin';
import BreakPlugin from './plugins/BreakPlugin';
import ShortcodePlugin from './plugins/ShortcodePlugin';
import { TablePlugin, TableRowPlugin, TableCellPlugin } from './plugins/TablePlugin';
import defaultEmptyBlock from './defaultEmptyBlock';
import { mergeMediaConfig } from './mergeMediaConfig';
import { handleLinkClick } from './linkHandler';
import { jsx as ___EmotionJSX } from "@emotion/react";
function editorStyles({
  minimal
}) {
  return /*#__PURE__*/css("position:relative;font-family:", fonts.primary, ";min-height:", minimal ? 'auto' : lengths.richTextEditorMinHeight, ";margin-top:-", editorStyleVars.stickyDistanceBottom, ";padding:0;display:flex;flex-direction:column;z-index:", zIndex.zIndex100, ";white-space:pre-wrap;;label:editorStyles;" + (process.env.NODE_ENV === "production" ? "" : "/*# sourceMappingURL=data:application/json;charset=utf-8;base64,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 */"));
}
const emptyValue = [defaultEmptyBlock()];
export default function VisualEditor(props) {
  const {
    t,
    field,
    className,
    isDisabled,
    isEditorComponent,
    onMode,
    isShowModeToggle,
    onChange,
    getEditorComponents,
    getAsset
  } = props;
  let editorComponents = getEditorComponents();
  const codeBlockComponent = fromJS(editorComponents.find(({
    type
  }) => type === 'code-block'));
  editorComponents = codeBlockComponent || editorComponents.has('code-block') ? editorComponents : editorComponents.set('code-block', {
    label: 'Code Block',
    type: 'code-block'
  });
  mergeMediaConfig(editorComponents, field);
  function handleToggleMode() {
    onMode('raw');
  }
  function handleChange({
    value
  }) {
    const mdValue = slateToMarkdown(value, {
      voidCodeBlock: !!codeBlockComponent
    }, editorComponents);
    onChange(mdValue);
  }
  const initialValue = props.value ? markdownToSlate(props.value, {
    editorComponents,
    voidCodeBlock: !!codeBlockComponent
  }) : emptyValue;
  const editor = usePlateEditor({
    override: {
      components: {
        [BoldPlugin.key]: withProps(PlateLeaf, {
          as: 'b'
        }),
        [CodePlugin.key]: CodeLeaf,
        [ItalicPlugin.key]: withProps(PlateLeaf, {
          as: 'em'
        }),
        [StrikethroughPlugin.key]: withProps(PlateLeaf, {
          as: 's'
        }),
        [ParagraphPlugin.key]: withProps(ParagraphElement, {
          as: 'p'
        }),
        [KEYS.h1]: withProps(HeadingElement, {
          variant: 'h1'
        }),
        [KEYS.h2]: withProps(HeadingElement, {
          variant: 'h2'
        }),
        [KEYS.h3]: withProps(HeadingElement, {
          variant: 'h3'
        }),
        [KEYS.h4]: withProps(HeadingElement, {
          variant: 'h4'
        }),
        [KEYS.h5]: withProps(HeadingElement, {
          variant: 'h5'
        }),
        [KEYS.h6]: withProps(HeadingElement, {
          variant: 'h6'
        }),
        ['ul']: withProps(ListElement, {
          variant: 'ul'
        }),
        ['ol']: withProps(ListElement, {
          variant: 'ol'
        }),
        ['li']: withProps(ListElement, {
          variant: 'li'
        }),
        ['blockquote']: BlockquoteElement,
        ['image']: withProps(ImageElement, {
          getAsset,
          field
        })
      }
    },
    plugins: [ParagraphPlugin, HeadingPlugin.configure({
      shortcuts: {
        h1: {
          keys: 'mod+1',
          handler: () => editor.tf.toggleBlock('h1')
        },
        h2: {
          keys: 'mod+2',
          handler: () => editor.tf.toggleBlock('h2')
        },
        h3: {
          keys: 'mod+3',
          handler: () => editor.tf.toggleBlock('h3')
        },
        h4: {
          keys: 'mod+4',
          handler: () => editor.tf.toggleBlock('h4')
        },
        h5: {
          keys: 'mod+5',
          handler: () => editor.tf.toggleBlock('h5')
        },
        h6: {
          keys: 'mod+6',
          handler: () => editor.tf.toggleBlock('h6')
        }
      }
    }), BoldPlugin, ItalicPlugin, StrikethroughPlugin.configure({
      shortcuts: {
        toggle: {
          keys: 'mod+shift+s'
        }
      }
    }), CodePlugin.configure({
      shortcuts: {
        toggle: {
          keys: 'mod+shift+c'
        }
      }
    }), ListPlugin, BreakPlugin, ImagePlugin, LinkPlugin.configure({
      node: {
        component: LinkElement
      },
      shortcuts: {
        toggleLink: {
          keys: 'mod+k',
          handler: () => {
            handleLinkClick({
              editor,
              t
            });
          }
        }
      }
    }), ExtendedBlockquotePlugin.configure({
      node: {
        component: BlockquoteElement
      }
    }), ShortcodePlugin, TablePlugin, TableRowPlugin, TableCellPlugin],
    value: initialValue
  });
  useEffect(() => {
    if (props.pendingFocus) {
      editor.tf.focus({
        edge: 'endEditor'
      });
      props.pendingFocus();
    }
  }, [props.pendingFocus]);
  return ___EmotionJSX(ClassNames, null, ({
    css,
    cx
  }) => ___EmotionJSX("div", {
    className: cx(className, css`
              ${editorContainerStyles}
            `)
  }, ___EmotionJSX(Plate, {
    editor: editor,
    onChange: handleChange
  }, ___EmotionJSX(EditorControlBar, null, ___EmotionJSX(Toolbar, {
    onToggleMode: handleToggleMode,
    buttons: field.get('buttons'),
    editorComponents: editorComponents,
    allowedEditorComponents: field.get('editor_components'),
    isShowModeToggle: isShowModeToggle,
    isEditorComponent: isEditorComponent,
    t: t,
    disabled: isDisabled
  })), ___EmotionJSX("div", {
    css: editorStyles({
      minimal: field.get('minimal')
    })
  }, ___EmotionJSX(Editor, {
    isDisabled: isDisabled
  })))));
}